import { DeleteOutlined, EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Modal, notification, Typography } from "antd";
import {
  MARINA_COVER_NOTE_BILL_DELETE_ENDPOINT,
  MARINA_COVER_NOTE_BILL_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MARINA_COVER_NOTE_BILL_CREATE_PERMISSION,
  MARINA_COVER_NOTE_BILL_DELETE_PERMISSION,
  MARINA_COVER_NOTE_BILL_EDIT_PERMISSION,
  MARINA_COVER_NOTE_BILL_VIEW_PERMISSION,
} from "routes/permissions";
import { formatNumberWithCommas, formatNumberWithCommasFloat, hasPermission } from "utils/functions";

function MarinaCoverNoteBill() {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  // Use reference equality to prevent unnecessary state updates
  const dataCache = useMemo(
    () => ({
      lastResponse: null,
      lastData: [],
      lastMetaData: null,
    }),
    [],
  );

  const getAllMarinaCoverNoteBills = useCallback(
    async (filterValues) => {
      const { b2b_lc_number, sales_contract_number, insurance_company, from_date, to_date } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      try {
        const query = `${MARINA_COVER_NOTE_BILL_LIST_ENDPOINT}?page=${currentPage}`;
        const bodyData = {
          ...(b2b_lc_number && { b2b_lc_id: b2b_lc_number }),
          ...(sales_contract_number && {
            sales_contract_id: sales_contract_number,
          }),
          ...(insurance_company && { insurance_company_id: insurance_company }),
          ...(from_date && { from_date }),
          ...(to_date && { to_date }),
        };

        const response = await getData(query, false, bodyData);

        if (response?.status === 200) {
          // Check if response data is actually different using reference equality
          if (dataCache.lastResponse === response.data) {
            setTableData(dataCache.lastData);
            setMetaData(dataCache.lastMetaData);
            setLoading(false);
            return;
          }

          const data = response.data?.data?.data || [];

          // Add unique key if id is missing
          const dataWithKeys = data.map((item, index) => ({
            ...item,
            key: item.id || `marine-cover-note-bill-${index}`,
          }));

          // Store in cache
          dataCache.lastResponse = response.data;
          dataCache.lastData = dataWithKeys;
          dataCache.lastMetaData = {
            current: response.data?.data?.current_page || 1,
            defaultCurrent: response.data?.data?.from || 1,
            defaultPageSize: response.data?.data?.per_page || 10,
            total: response.data?.data?.total || 0,
          };

          setTableData(dataWithKeys);
          setMetaData(dataCache.lastMetaData);
        } else {
          setFetchError("Could not load this list.");
        }
      } catch (error) {
        console.error("Error fetching marine cover note bills:", error);
        setFetchError("Could not load this list.");
        notification.error({
          message: "Error",
          description: "Failed to load marine cover note bills",
          placement: "topRight",
        });
      } finally {
        setLoading(false);
      }
    },
    [currentPage, dataCache],
  );

  // Effects
  useEffect(() => {
    getAllMarinaCoverNoteBills(filterData);
  }, [refresh, filterData, getAllMarinaCoverNoteBills]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedMarinaCoverNoteBill = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this Marine Cover Note Bill?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(`${MARINA_COVER_NOTE_BILL_DELETE_ENDPOINT}/${id}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "Marine Cover Note Bill deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/marine-cover-note-bill/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/marine-cover-note-bill/edit/${record.id}`);
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/marine-cover-note-bill/view/${record.id}`);
    },
    [navigate],
  );

  // Memoize the changePage function to prevent unnecessary recreation
  const changePage = useCallback(
    (newPage) => {
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    },
    [currentPage],
  );

  // Memoize action handlers to avoid recreation in render cycle
  const handleView = useCallback((record) => navigateToView(record), [navigateToView]);
  const handleEdit = useCallback((record) => navigateToEdit(record), [navigateToEdit]);
  const handleDelete = useCallback(
    (record) => deleteSelectedMarinaCoverNoteBill(record.id),
    [deleteSelectedMarinaCoverNoteBill],
  );

  // Create menu items factory outside render cycle
  const getMenuItems = useCallback(
    (record) => [
      {
        key: "view",
        label: "View",
        icon: <EyeOutlined />,
        onClick: () => handleView(record),
        disabled: !hasPermission([MARINA_COVER_NOTE_BILL_VIEW_PERMISSION]),
      },
      {
        key: "edit",
        label: "Edit",
        icon: <EditOutlined />,
        onClick: () => handleEdit(record),
        disabled: !hasPermission([MARINA_COVER_NOTE_BILL_EDIT_PERMISSION]),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined />,
        onClick: () => handleDelete(record),
        disabled: !hasPermission([MARINA_COVER_NOTE_BILL_DELETE_PERMISSION]),
        danger: true,
      },
    ],
    [handleView, handleEdit, handleDelete],
  );

  // Memoize the entire columns definition
  const tableColumns = useMemo(
    () => [
      {
        title: "Insurance Company",
        dataIndex: ["company", "name"],
        width: "15%",
        key: "companyName",
        ellipsis: true,
        render: (name) => name || "N/A",
      },
      {
        title: "From Date",
        dataIndex: "from_date",
        width: "12%",
        key: "fromDate",
        ellipsis: true,
        render: (date) => (date ? moment(date).format("DD MMM YYYY") : "N/A"),
      },
      {
        title: "To Date",
        dataIndex: "to_date",
        width: "12%",
        key: "toDate",
        ellipsis: true,
        render: (date) => (date ? moment(date).format("DD MMM YYYY") : "N/A"),
      },
      {
        title: "Total Bill Amount",
        dataIndex: "total_bill_amount",
        width: "15%",
        key: "totalBillAmount",

        render: (_, obj) => {
          console.log("🚀 ~ MarinaCoverNoteBill ~ obj:", obj);
          const totalSum = obj?.marine_details?.reduce((sum, item) => sum + (item.total || 0), 0);
          return totalSum ? formatNumberWithCommas(totalSum) : "0.00";
        },
      },

      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => (
          <Dropdown menu={{ items: getMenuItems(record) }} placement="bottomRight" trigger={["hover"]}>
            <Button type="default" icon={<MoreOutlined />} />
          </Dropdown>
        ),
      },
    ],
    [getMenuItems], // Only depend on getMenuItems which has stable references
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllMarinaCoverNoteBills}
        filterName="marina_cover_note_bill"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={navigateToAdd}
                disabled={!hasPermission([MARINA_COVER_NOTE_BILL_CREATE_PERMISSION])}
                addButtonText={hasPermission([MARINA_COVER_NOTE_BILL_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Marine Cover Note Bill</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllMarinaCoverNoteBills(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
    </>
  );
}

// Export with memo for performance optimization
export default React.memo(MarinaCoverNoteBill);
